import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Moment } from "moment";

import EntityChart from "../entityChart";
import EntityChartFilters from "../entityChartFilters";
import ReportListView from "../reportListView";
import ReportListViewFilters from "../reportListViewFilters";
import Contact from "src/components/contact";
import AssetCard from "../assetCard";
import PopularViewersListItem from "../popularViewersListItem";
import TopViewerCard from "../topViewerCard";
import TopViewerListItem from "../topViewerListItem";
import TopAccountCard from "../topAccounts";
import TopAccountListItem from "../topAccountsList";
import CampaignCard from "../campaignCard";
import CampaignListItem from "../campaignListItem";
import ConversationActivityCard from "../conversationCard";
import ConversationActivityListItem from "../conversationActivityList";
import CollectionActivityCard from "../collectionActivityCard";
import CollectionActivityListItem from "../collectionActivityListItem";
import PopularAssetCard from "../popularAssetCard";
import {
  fetchEngagementAssetViewsGraphReport,
  fetchEngagementAttentionTimeGraphReport,
  fetchEngagementCampaignSources,
  fetchEngagementCampaigns,
  fetchEngagementCollectionActivityReport,
  fetchEngagementDealRoomActivityReport,
  fetchEngagementPopularAssets,
  fetchEngagementShareGraphReport,
  fetchEngagementShareReport,
  fetchEngagementTopAccounts,
  fetchEngagementTopUsers,
  fetchEngagementUniqueViewersGraphReport,
} from "src/redux/actions/reports";
import {
  CAMPAIGNS_SCHEMA,
  COLLECTION_ACTIVITY_SCHEMA,
  CONTENT_SHARED_SCHEMA,
  CONVERSATION_ACTIVITY_SCHEMA,
  DEAL_ROOM_ACTIVITY_SCHEMA,
  POPULAR_ASSET_SCHEMA,
  TOP_ACCOUNTS_SCHEMA,
  VIEWERS_SCHEMA,
} from "src/constants/reports";

import { useReportsAdoption } from "src/redux/actions/adoption/reports";

import userRoleService from "src/utils/userRoles.service";

import _ from "lodash";
import cx from "classnames";
import styles from "./engagement.module.scss";
import { EngagementTables, ReportTabs } from "src/types/reports";
import { DealRoomCard } from "../dealRoomCard";
import { DealRoomListItem } from "../dealRoomListItem";
interface EngagementTabProps {
  reportDate?: ReportDate;
  filters?: any;
  interval?: string;
}

interface ReportDate {
  startDate: Moment;
  endDate: Moment;
  type?: string;
}

const DefaultAssetIcon = "/images/defaultIcons/default_asset.jpg";

const EngagementTab: React.FC<EngagementTabProps> = ({
  reportDate,
  filters,
  interval,
}) => {
  const dispatch = useDispatch();

  const { trackReportsToggledView } = useReportsAdoption();

  const [selectedFilter, setSelectedFilter] = useState("all");
  const [listFilter, setListFilter] = useState("views");
  const [contactInfo, setContactInfo] = useState({
    show: false,
    contactId: "",
  });

  const { engagement } = useSelector((state: any) => ({
    engagement: _.get(state, "reports.engagement", {}),
  }));

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
  };

  const handleListFilterChange = (value) => {
    setListFilter(value);
  };

  const validFilters = useMemo(() => {
    if (!filters || !Array.isArray(filters)) return [];

    return filters.filter((f) => {
      const { property, operator, value } = f || {};
      return (
        property?.type &&
        operator &&
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((v) => typeof v === "string" && v.trim() !== "")
      );
    });
  }, [filters]);

  const {
    attentionTime,
    uniqueViewers,
    sharesGraph,
    assetViewsGraph,
    popularAssets,
    topUsers,
    topAccounts,
    campaigns,
    campaignSources,
    conversationActivities,
    collectionActivities,
    dealRoomActivities
  } = engagement;

  const baseFilters = useMemo(() => {
    if (!reportDate || !reportDate.startDate || !reportDate.endDate)
      return null;

    const formattedStartDate = reportDate.startDate.format("YYYY-MM-DD");
    const formattedEndDate = reportDate.endDate.format("YYYY-MM-DD");

    return {
      startDate: `${formattedStartDate}T00:00:00+00:00`,
      endDate: `${formattedEndDate}T23:59:59+00:00`,
      filters: filters || [],
      sortBy: "views",
      userType: selectedFilter,
      interval: interval || "day",
    };
  }, [reportDate, selectedFilter, validFilters, interval]);

  const listBaseFilters = useMemo(() => {
    if (!reportDate || !reportDate.startDate || !reportDate.endDate)
      return null;

    const formattedStartDate = reportDate.startDate.format("YYYY-MM-DD");
    const formattedEndDate = reportDate.endDate.format("YYYY-MM-DD");

    return {
      startDate: `${formattedStartDate}T00:00:00+00:00`,
      endDate: `${formattedEndDate}T23:59:59+00:00`,
      filters: filters || [],
      interval: interval || "day",
      sortBy: listFilter,
      userType: "all",
    };
  }, [listFilter,reportDate,validFilters, interval]);

  const handleLoadMore = (assetType) => {

    switch (assetType) {
      case "engagementPopularAssets":
        if (popularAssets.pagination.currentPage < popularAssets.pagination.totalPages) {
          const request = {
            page: popularAssets.pagination.currentPage + 1,
            filter: listBaseFilters || baseFilters,
            limit: 20,
          };
          dispatch(fetchEngagementPopularAssets(request));
        }
        break;
      case "engagementTopUsers":
        if (topUsers.pagination.currentPage < topUsers.pagination.totalPages) {
          const request = {
            page: topUsers.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchEngagementTopUsers(request));
        }
        break;
      case "engagementTopAccounts":
        if (topAccounts.pagination.currentPage < topAccounts.pagination.totalPages) {
          const request = {
            page: topAccounts.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchEngagementTopAccounts(request));
        }
        break;
      case "campaigns":
        if (campaigns.pagination.currentPage < campaigns.pagination.totalPages) {
          const request = {
            page: campaigns.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchEngagementCampaigns(request));
        }
        break;
      case "campaignSources":
        if (campaignSources.pagination.currentPage < campaignSources.pagination.totalPages) {
          const request = {
            page: campaignSources.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchEngagementCampaignSources(request));
        }
        break;
      case "conversationActivities":
        if (conversationActivities.pagination.currentPage < conversationActivities.pagination.totalPages) {
          const request = {
            page: conversationActivities.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchEngagementShareReport(request));
        }
        break;
      case "collectionActivities":
        if (collectionActivities.pagination.currentPage < collectionActivities.pagination.totalPages) {
          const request = {
            page: collectionActivities.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchEngagementCollectionActivityReport(request));
        }
        break;
      case "dealRoomActivities":
        if (dealRoomActivities.pagination.currentPage < dealRoomActivities.pagination.totalPages) {
          const request = {
            page: dealRoomActivities.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchEngagementDealRoomActivityReport(request));
        }
        break;
      default:
        return;
    }
  };

  const handleViewMore = (assetType) => {
    let request;
  
    switch (assetType) {
      case "engagementPopularAssets":
        request = {
          page: 0,
          limit: 20,
          filter: listBaseFilters || baseFilters,
        };
        dispatch(fetchEngagementPopularAssets(request));
        break;
  
      case "engagementTopUsers":
        request = {
          page: 0,
          limit: 20,
          filter: baseFilters,
        };
        dispatch(fetchEngagementTopUsers(request));
        break;
  
      case "engagementTopAccounts":
        request = {
          page: 0,
          limit: 20,
          filter: baseFilters,
        };
        dispatch(fetchEngagementTopAccounts(request));
        break;
  
      case "campaigns":
        request = {
          page: 0,
          limit: 20,
          filter: baseFilters,
        };
        dispatch(fetchEngagementCampaigns(request));
        break;
  
      case "campaignSources":
        request = {
          page: 0,
          limit: 20,
          filter: baseFilters,
        };
        dispatch(fetchEngagementCampaignSources(request));
        break;
  
      case "conversationActivities":
        request = {
          page: 0,
          limit: 20,
          filter: baseFilters,
        };
        dispatch(fetchEngagementShareReport(request));
        break;
  
      case "collectionActivities":
        request = {
          page: 0,
          limit: 20,
          filter: baseFilters,
        };
        dispatch(fetchEngagementCollectionActivityReport(request));
        break;
        
      case "dealRoomActivities":
        request = {
          page: 0,
          limit: 20,
          filter: baseFilters,
        };
        dispatch(fetchEngagementDealRoomActivityReport(request));
        break;
  
      default:
        return;
    }
  };
  

  useEffect(() => {
    if (!baseFilters) return;

    trackReportsToggledView({ tabType: _.capitalize(ReportTabs.engagement) });
  
    dispatch(fetchEngagementAttentionTimeGraphReport({filter: baseFilters }));
    dispatch(fetchEngagementUniqueViewersGraphReport({filter: baseFilters }));
    dispatch(fetchEngagementShareGraphReport({filter: baseFilters }));
    dispatch(fetchEngagementAssetViewsGraphReport({filter: baseFilters }));
    dispatch(fetchEngagementPopularAssets({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchEngagementTopUsers({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchEngagementTopAccounts({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchEngagementCampaigns({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchEngagementCampaignSources({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchEngagementShareReport({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchEngagementCollectionActivityReport({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchEngagementDealRoomActivityReport({ page: 0, limit: 5, filter: baseFilters }));
  },[JSON.stringify(baseFilters)]);
  
  useEffect(() => {
    if (selectedFilter) {
      dispatch(
        fetchEngagementUniqueViewersGraphReport({ filter: baseFilters }),
      );
    }
  }, [selectedFilter]);

  useEffect(() => {
    if (listFilter) {
      dispatch(
        fetchEngagementPopularAssets({
          page: 0,
          limit: 5,
          filter: listBaseFilters,
        }),
      );
    }
  }, [listFilter, listBaseFilters]);

  return (
    <div className={styles.cardsContainer}>
      <EntityChart
        title={`Avg.Attention Time`}
        loading={attentionTime?.loading}
        description="The average duration of asset engagement by end users in fliteview"
        data={attentionTime?.item}
        legend={{ key: "Date", value: "Avg.time (mm:ss)" }}
        valueFormatter={(value: number) => {
          if (typeof value !== "number" || value <= 0) return "00:00";
          const minutes = Math.floor(value / 60);
          const seconds = Math.round(value % 60);
          return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        }}
        yAxisFormatter={(value) => {
          if (typeof value !== "number" || value <= 0) return "00:00";
          const minutes = Math.floor(value / 60);
          const seconds = Math.round(value % 60);
          return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        }}
        tabName={ReportTabs.engagement}
        tableName={EngagementTables.avgAttentionTime}
      />
      <EntityChartFilters
        title={`# of unique viewers (${uniqueViewers?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Daily count of unique viewers who interacted with content in fliteview"
        data={uniqueViewers?.item}
        legend={{ key: "Date", value: "Unique viewers" }}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        filterOptions={[
          { value: "all", label: "All viewers" },
          { value: "known", label: "Known viewers" },
          { value: "anonymous", label: "Anonymous viewers" },
        ]}
        defaultFilter="all"
        selectedFilter={selectedFilter}
        onFilterChange={handleFilterChange}
        loading={uniqueViewers?.loading}
          tabName={ReportTabs.engagement}
        tableName={EngagementTables.uniqueViewers}
      />
      <EntityChart
        title={`# of shares (${sharesGraph?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Daily count of shares made , offering insights into content distribution trends"
        data={sharesGraph?.item}
        legend={{ key: "Date", value: "Shares" }}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        loading={sharesGraph?.loading}
             tabName={ReportTabs.engagement}
        tableName={EngagementTables.shares}
      />
      <EntityChart
        title={`# of Asset Views (${assetViewsGraph?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Daily count of total unique asset views"
        data={assetViewsGraph?.item}
        legend={{ key: "Date", value: "Asset Views" }}
        containerClassName={styles.chartContainer}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        loading={assetViewsGraph?.loading}
        tabName={ReportTabs.engagement}
        tableName={EngagementTables.assetViews}
      />
      <div className={cx(styles.card, styles.emptyCard)}></div>

      <ReportListViewFilters
        title={`What's popular with your viewers? (${popularAssets.pagination.totalRecordsCount})`}
        description="Discover which assets shared received the maximum engagement, with the option to sort by views, average time spent and downloads"
        items={popularAssets.items}
        loading={popularAssets.loading}
        pagination={popularAssets.pagination}
        reportDate={reportDate}
        filters= {validFilters}
        loadMore={() => handleLoadMore("engagementPopularAssets")}
        viewMore={() => handleViewMore("engagementPopularAssets")}
        downloadConfig={{
          name: "engagement_assets",
          schema: POPULAR_ASSET_SCHEMA,
          fileName: "EngagementPopularAssets",
          title: "Popular Assets Report",
          reportType: "engamentPopularAssets",
        }}
        hasFilters={true}
        canDownload={_.get(popularAssets, 'pagination.totalRecordsCount', 0) <= 5}
        renderItem={({ item }) => <PopularAssetCard item={item} />}
        renderScrollItem={({ item, columnCount }) => (
          <PopularViewersListItem item={item} columnCount={columnCount} />
        )}
        filterOptions={[
          { value: "views", label: "Views" },
          { value: "avgTime", label: "Avg Time" },
          { value: "downloads", label: "Downloads" },
        ]}
        defaultFilter="views"
        selectedFilter={listFilter}
        onFilterChange={handleListFilterChange}
        columns={["Views", "Avg View Time", "# of shares", "# of downloads"]}
        firstColumn="Asset Name"
             tabName={ReportTabs.engagement}
        tableName={EngagementTables.popularViewers}
      />

      <ReportListView
        title={`Who are your top known viewers? (${topUsers.pagination.totalRecordsCount})`}
        description="Known users with the highest asset views in fliteview"
        items={topUsers.items}
        loading={topUsers.loading}
        pagination={topUsers.pagination}
        reportDate={reportDate}
        filters={validFilters}
        loadMore={() => handleLoadMore("engagementTopUsers")}
        viewMore={() => handleViewMore("engagementTopUsers")}
        downloadConfig={{
          name: "engagement_viewers",
          schema: VIEWERS_SCHEMA,
          fileName: "EngagementTopUsers",
          title: "Top Known Viewers Report",
          reportType: "engagementTopUsers",
        }}
        canDownload={_.get(topUsers, 'pagination.totalRecordsCount', 0) <= 5}
        renderItem={({ item }) => <TopViewerCard item={item} onClick={() => setContactInfo({ show: true, contactId: item.contact.id })} />}
        renderListItem={({ item, columnCount }) => (
          <TopViewerListItem item={item} columnCount={columnCount} onClick={() => setContactInfo({ show: true, contactId: item.contact.id })}  />
        )}
        columns={["Views", "Avg View Time", "# of reshares"]}
        firstColumn="Top Known Viewers"
            tabName={ReportTabs.engagement}
        tableName={EngagementTables.topViewers}
      />

      <ReportListView
        title={`Who are your top accounts? (${topAccounts.pagination.totalRecordsCount})`}
        description="Accounts with the highest cumulative asset views across users"
        items={topAccounts.items}
        loading={topAccounts.loading}
        pagination={topAccounts.pagination}
        reportDate={reportDate}
        filters={validFilters}
        loadMore={() => handleLoadMore("engagementTopAccounts")}
        viewMore={() => handleViewMore("engagementTopAccounts")}
        downloadConfig={{
          name: "engagement_accounts",
          schema: TOP_ACCOUNTS_SCHEMA,
          fileName: "EngagementTopAccounts",
          title: "Top Accounts Report",
          reportType: "engagementTopAccounts",
        }}
        canDownload={_.get(topAccounts, 'pagination.totalRecordsCount', 0) <= 5}
        renderItem={({ item }) => <TopAccountCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <TopAccountListItem item={item} columnCount={columnCount} />
        )}
        columns={["Views", "Avg View Time", "# of shares"]}
        firstColumn="Top Accounts"
           tabName={ReportTabs.engagement}
        tableName={EngagementTables.topAccounts}
      />

      <ReportListView
        title={`How are your campaigns performing ? (${campaigns.pagination.totalRecordsCount})`}
        description="Top performing campaigns based on views"
        items={campaigns.items.map((item, index) => ({
          ...item,
          manualIndex: index + 1,
        }))}
        loading={campaigns.loading}
        pagination={campaigns.pagination}
        reportDate={reportDate}
        filters={validFilters}
        loadMore={() => handleLoadMore("campaigns")}
        viewMore={() => handleViewMore("campaigns")}
        downloadConfig={{
          name: "engagement_campaigns",
          schema: CAMPAIGNS_SCHEMA,
          fileName: "EngagementCampaigns",
          title: "Campaigns Report",
          reportType: "engagementCampaigns",
        }}
        canDownload={_.get(campaigns, 'pagination.totalRecordsCount', 0) <= 5}
        renderItem={({ item }) => <CampaignCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <CampaignListItem item={item} columnCount={columnCount} />
        )}
        columns={["Views", "Avg View Time", "# of assets"]}
        firstColumn="Campaign Name"
          tabName={ReportTabs.engagement}
          tableName={EngagementTables.campaignPerforming}
      />

      <ReportListView
        title={`How are your content shared? (${campaignSources.pagination.totalRecordsCount})`}
        description="The most preferred methods for sharing content"
        items={campaignSources.items.map((item, index) => ({
          ...item,
          manualIndex: index + 1,
        }))}
        loading={campaignSources.loading}
        pagination={campaignSources.pagination}
        reportDate={reportDate}
        filters={validFilters}
        loadMore={() => handleLoadMore("campaignSources")}
        viewMore={() => handleViewMore("campaignSources")}
        downloadConfig={{
          name: "engagement_channels",
          schema: CONTENT_SHARED_SCHEMA,
          fileName: "EngagementCampaignSources",
          title: "Campaign Sources Report",
          reportType: "engagementCampaignSources",
        }}
        canDownload={_.get(campaignSources, 'pagination.totalRecordsCount', 0) <= 5}
        renderItem={({ item }) => <CampaignCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <CampaignListItem item={item} columnCount={columnCount} />
        )}
        columns={["Views", "Avg View Time", "# of assets"]}
        firstColumn="Campaign Source"
           tabName={ReportTabs.engagement}
        tableName={EngagementTables.contentShared}
        
      />

      <ReportListView
        title={`Conversation Activities (${conversationActivities.pagination.totalRecordsCount})`}
        description="Most recent shares along with their engagement metrics"
        items={conversationActivities.items}
        loading={conversationActivities.loading}
        pagination={conversationActivities.pagination}
        loadMore={() => handleLoadMore("conversationActivities")}
        viewMore={() => handleViewMore("conversationActivities")}
        reportDate={reportDate}
        filters={validFilters}
        downloadConfig={{
          name: "engagement_conversations",
          schema: CONVERSATION_ACTIVITY_SCHEMA,
          fileName: "EngagementConversationActivities",
          title: "Conversation Activities Report",
          reportType: "engagementConversationActivities",
        }}
        canDownload={_.get(conversationActivities, 'pagination.totalRecordsCount', 0) <= 5}
        renderItem={({ item }) => <ConversationActivityCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <ConversationActivityListItem item={item} columnCount={columnCount} />
        )}
        columns={[
          "Created By",
          "Created On",
          "Shared Via",
          "Open Rate",
          "Click Rate",
          "Time Spent",
        ]}
        firstColumn="Conversation"
         tabName={ReportTabs.engagement}
        tableName={EngagementTables.conversationActivity}
      />

      {userRoleService.canAccessCollection() && (
      <ReportListView
        title={`Collection Activity (${collectionActivities.pagination.totalRecordsCount})`}
        description="Collection created with the highest engagement activities"
        items={collectionActivities.items}
        loading={collectionActivities.loading}
        pagination={collectionActivities.pagination}
        loadMore={() => handleLoadMore("collectionActivities")}
        viewMore={() => handleViewMore("collectionActivities")}
        reportDate={reportDate}
        filters={validFilters}
        downloadConfig={{
          name: "engagement_collections",
          schema: COLLECTION_ACTIVITY_SCHEMA,
          fileName: "EngagementCollectionActivities",
          title: "Collection Activities Report",
          reportType: "engagementCollectionActivities",
        }}
        canDownload={_.get(collectionActivities, 'pagination.totalRecordsCount', 0) <= 5}
        renderItem={({ item }) => <CollectionActivityCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <CollectionActivityListItem item={item} columnCount={columnCount} />
        )}
        columns={[
          "Created By",
          "Created On",
          "Last Updated",
          "Shares",
          "Views",
        ]}
        firstColumn="Collection"
            tabName={ReportTabs.engagement}
        tableName={EngagementTables.collectionActivity}
      />
      )}

      {userRoleService.canAccessDealRoom() && (
      <ReportListView
        title={`Deal Room Activity (${dealRoomActivities.pagination.totalRecordsCount})`}
        description="Deal room created with the highest engagement activities"
        items={dealRoomActivities.items}
        loading={dealRoomActivities.loading}
        pagination={dealRoomActivities.pagination}
        loadMore={() => handleLoadMore("dealRoomActivities")}
        viewMore={() => handleViewMore("dealRoomActivities")}
        reportDate={reportDate}
        filters={validFilters}
        downloadConfig={{
          name: "engagement_deal_rooms",
          schema: DEAL_ROOM_ACTIVITY_SCHEMA,
          fileName: "EngagementDealRoomActivities",
          title: "Deal room Activities Report",
          reportType: "engagementDealRoomActivities",
        }}
        canDownload={_.get(dealRoomActivities, 'pagination.totalRecordsCount', 0) <= 5}
        renderItem={({ item }) => <DealRoomCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <DealRoomListItem item={item} columnCount={columnCount} />
        )}
        columns={[
          "Created By",
          "Created On",
          "Last Updated",
          "Views",
          "Stakeholders",
          "Interactions"
        ]}
        firstColumn="Deal room"
        tabName={ReportTabs.engagement}
        tableName={EngagementTables.dealRoomActivity}
      />
      )}
      <Contact
        show={contactInfo.show}
        contactId={contactInfo.contactId}
        onClose={() => setContactInfo({ show: false, contactId: "" })}
      />
    </div>
  );
};

export default EngagementTab;
